import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { CashInflowFilters, CashInflowPayment } from "../../utils/cashinflow.types";
import { PaymentReceipt, PaymentReceiptApplication } from "../../utils/invoice.types";
import { IDeleteApiResponse, IError } from "../../utils/api.types";

export const fetchCashOutflowPaymentsDetails = createAsyncThunk(
    'cashOutflow/vendorPayments',
    async (filters: CashInflowFilters): Promise<any> => {
        let paramsArry = [];
        if (filters.selectedBuyer) {
            let q1 = ['supplier', filters.selectedBuyer];
            paramsArry.push(q1);
        }

        if (filters.selectedInvoiceStatus) {
            let q2 = ['status', filters.selectedInvoiceStatus];
            paramsArry.push(q2);
        }

        if (filters.selectedAmountRange) {
            let t = null;
            if (filters.selectedAmountRange.indexOf('-') >= 0) {
                t = filters.selectedAmountRange.split("-");
            } else if (filters.selectedAmountRange.indexOf('&') >= 0) {
                t = filters.selectedAmountRange.split("&");
            }

            if (t && t.length == 2) {
                let fromAmountRange = t[0];
                let toAmountRange = t[1];
                let q31 = ['fromAmountRange', fromAmountRange.trim()];
                let q32 = ['toAmountRange', toAmountRange.trim()];
                paramsArry.push(q31);
                paramsArry.push(q32);
            } else {
                t = filters.selectedAmountRange.split("&");
            }
        }

        if (filters.selectedPaymentMode) {
            paramsArry.push(['paymentMode', filters.selectedPaymentMode]);
        }

        if (filters.startDate) {
            let q4 = ['startDate', filters.startDate.getTime()];
            paramsArry.push(q4);
        }

        if (filters.endDate) {
            let q5 = ['endDate', filters.endDate.getTime()];
            paramsArry.push(q5);
        }

        // Add pagination parameters
        const limit = filters.limit || 50;
        const offset = filters.offset || 0;
        paramsArry.push(['limit', limit.toString()]);
        paramsArry.push(['offset', offset.toString()]);
        if (filters.paymentId) paramsArry.push(['paymentId', filters.paymentId]);

        const params = new URLSearchParams(paramsArry);

        const response = await axios.get(`/api/v1/invoice/vendor-payment-application`, {params});
        return response.data;
    }
);

/**
 * Payment history of a single purchase invoice (each applied vendor payment, oldest first).
 * Used by the Paid / Partially Paid status tooltip on the invoice list.
*/
export const fetchPurchaseInvoicePaymentHistory = createAsyncThunk(
    'cashOutflow/fetchPurchaseInvoicePaymentHistory',
    async (invoiceId: string): Promise<{ payments: any[] }> => {
        const response = await axios.get(`/api/v1/invoice/vendor-payment-application/by-invoice/${invoiceId}`);
        return response.data;
    }
);

export const addCashOutflowPayment = createAsyncThunk(
    'cashInflow/addOutflowPayment',
    async (data: CashInflowPayment): Promise<CashInflowPayment> => {
        const response = await axios.post('/api/v1/invoice/vendor-payment', data);
        return response.data;
    }
);

export const attachInvoiceToVendorPayment = createAsyncThunk(
    'invoice/attachInvoiceToVendorPayment',
    async (payload: any): Promise<IDeleteApiResponse> => {
        const response = await axios.post('/api/v1/invoice/vendor-payment-application', payload);
        return response.data
    }
);

// export const addCashInflowPayment = createAsyncThunk(
//     'cashInflow/addInflowPayment',
//     async (data: CashInflowPayment): Promise<CashInflowPayment> => {
//         const response = await axios.post('/api/v1/invoice/payment-receipt', data);
//         return response.data;
//     }
// );

// export const attachInvoiceToPayment = createAsyncThunk(
//     'cashInflow/attachInvoiceToPayment',
//     async (data: PaymentReceiptApplication): Promise<PaymentReceipt> => {
//         const response = await axios.post('/api/v1/invoice/payment-receipt-application', data);
//         return response.data;
//     }
// );

export const deletePaymentReceiptOutflow = createAsyncThunk<IDeleteApiResponse,
string,
{
    rejectValue: IError,
}
>(
    'cashOutflow/deletePaymentReceipt',
    async (paymentId: string, thunkApi) => {
        try {
            const response = await axios.delete(`/api/v1/invoice/vendor-payment/delete-entire/${paymentId}`);
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

// Add basic vendor payment deletion function
export const deleteVendorPayment = createAsyncThunk<IDeleteApiResponse,
string,
{
    rejectValue: IError,
}
>(
    'cashOutflow/deleteVendorPayment',
    async (invoiceId: string, thunkApi) => {
        try {
            const response = await axios.delete(`/api/v1/invoice/vendor-payment?invoiceId=${invoiceId}`);
            const { data } = response;
            if (response.status === 200) {
                response.data['id'] = invoiceId;
                return response.data
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

// Add vendor payment PDF creation function
export const createVendorPaymentPdf = createAsyncThunk(
    'cashOutflow/createVendorPaymentPdf',
    async (params: string): Promise<any> => {
        const response = await axios.get(`/api/v1/invoice/vendor-payment-application/pdf/create?${params}`);
        return response.data;
    }
);